import React, { useState } from 'react';
import { Check, Loader2, Star } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { TOOL_LABELS } from './ProgramCard';

const TierCard = ({ t, current, busy, onJoin }) => (
  <div className={`relative rounded-2xl border p-5 flex flex-col transition-transform hover:-translate-y-0.5 ${t.popular ? 'ring-2 ring-[#1D4ED8]' : ''}`}
    style={{ background: 'var(--p-surface)', borderColor: 'var(--p-border)' }} data-testid={`membership-tier-${t.tier}`}>
    {t.popular && <span className="absolute -top-3 left-5 inline-flex items-center gap-1 bg-[#1D4ED8] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"><Star size={10} fill="currentColor" /> Best value</span>}
    <div className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{t.name}</div>
    <div className="flex items-baseline gap-1 mt-2 mb-3"><span className="text-3xl font-bold">${t.price}</span><span className="text-sm" style={{ color: 'var(--p-faint)' }}>/month</span></div>
    <ul className="space-y-1.5 mb-5 flex-1">
      {t.perks.map((x) => <li key={x} className="flex gap-2 text-sm" style={{ color: 'var(--p-muted)' }}><Check size={14} className="text-emerald-500 mt-0.5 shrink-0" />{x}</li>)}
    </ul>
    <button onClick={() => onJoin(t)} disabled={busy || current} data-testid={`membership-join-${t.tier}`}
      className="w-full py-2.5 rounded-xl text-sm font-semibold border transition-colors disabled:opacity-60 hover:bg-[#1D4ED8] hover:text-white hover:border-[#1D4ED8]"
      style={{ borderColor: 'var(--p-border)' }}>
      {current ? 'Your current plan' : busy ? 'Opening checkout...' : 'Start membership'}
    </button>
  </div>
);

export default function MembershipSection({ tiers, choices, membership, busy, onCheckout, onCancel }) {
  const [picking, setPicking] = useState(null);
  const [sel, setSel] = useState([]);
  const active = membership?.status === 'active' ? membership : null;
  const join = (t) => { if (t.pick) { setSel([]); setPicking(t); } else onCheckout(t, []); };
  const toggle = (k) => setSel((s) => s.includes(k) ? s.filter((x) => x !== k) : s.length < picking.pick ? [...s, k] : s);
  return (
    <section id="memberships" className="mt-14 scroll-mt-24" data-testid="membership-section">
      <span className="text-xs tracking-[0.2em] uppercase text-[#1D4ED8] font-semibold">Tool memberships</span>
      <h2 className="text-2xl sm:text-3xl tracking-tight font-light mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Just want the tools? Go monthly.</h2>
      <p className="mt-2 mb-6 max-w-2xl text-sm" style={{ color: 'var(--p-muted)' }}>Use David's coaching tools on your own schedule. No long-term commitment, cancel anytime.</p>
      {active && (
        <div className="rounded-xl border p-4 mb-5 flex flex-wrap items-center justify-between gap-3" style={{ borderColor: 'rgba(16,185,129,0.4)', background: 'rgba(16,185,129,0.08)' }} data-testid="membership-current">
          <div className="text-sm"><strong>{active.name}</strong> membership is active · {(active.tools || []).map((t) => TOOL_LABELS[t]).join(', ')}
            {active.cancel_at_period_end && <span className="ml-2 text-[#F97316]">(ends at period end)</span>}</div>
          {!active.cancel_at_period_end && <button onClick={onCancel} className="text-xs font-semibold text-red-500 hover:underline" data-testid="membership-cancel">Cancel membership</button>}
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {tiers.map((t) => <TierCard key={t.tier} t={t} current={active?.tier === t.tier} busy={busy === t.tier} onJoin={join} />)}
      </div>
      <Dialog open={!!picking} onOpenChange={(o) => !o && setPicking(null)}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="membership-tool-picker">
          <DialogHeader>
            <DialogTitle>Choose {picking?.pick} tool{picking?.pick > 1 ? 's' : ''}</DialogTitle>
            <DialogDescription>{picking?.name} · ${picking?.price}/month</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {choices.map((k) => (
              <button key={k} onClick={() => toggle(k)} data-testid={`membership-pick-${k}`}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border text-sm text-left transition-colors ${sel.includes(k) ? 'border-[#1D4ED8] bg-blue-50 text-[#1D4ED8] font-semibold' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}>
                {TOOL_LABELS[k]} {sel.includes(k) && <Check size={15} />}
              </button>
            ))}
          </div>
          <button disabled={sel.length !== picking?.pick || !!busy} onClick={() => onCheckout(picking, sel)} data-testid="membership-pick-continue"
            className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-[#1D4ED8] text-white font-semibold rounded-lg py-2.5 disabled:opacity-50">
            {busy ? <Loader2 size={15} className="animate-spin" /> : null} Continue to checkout ({sel.length}/{picking?.pick})
          </button>
        </DialogContent>
      </Dialog>
    </section>
  );
}
